/**
 * Keyword Generation Service
 * Handles automatic keyword generation via n8n workflow
 */

const N8N_WEBHOOK_URL = 'https://n8n.mrprimo.org/webhook/keyword-generation';

const KEYWORD_WEBHOOK_TIMEOUT_MS = 15000;

interface KeywordGenerationData {
  business_id: string | number;
  business_type: 'trader' | 'craftie' | 'hospitality' | 'professional';
  data: {
    store_name: string;
    description: string;
    location: string;
  };
}

/**
 * Trigger keyword generation for a business
 * Non-blocking - won't throw errors to prevent disrupting main operations
 */
export const triggerKeywordGeneration = async (
  businessId: string | number,
  businessType: 'trader' | 'craftie' | 'hospitality' | 'professional',
  businessData: {
    store_name: string;
    description: string;
    location: string;
  }
): Promise<boolean> => {
  try {
    console.log('Triggering keyword generation:', {
      businessId,
      businessType,
      businessName: businessData.store_name
    });

    const payload: KeywordGenerationData = {
      business_id: String(businessId),
      business_type: businessType,
      data: businessData
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), KEYWORD_WEBHOOK_TIMEOUT_MS);

    const response = await fetch(N8N_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
      signal: controller.signal
    }).finally(() => {
      clearTimeout(timeoutId);
    });

    if (response.ok) {
      console.log('✅ Keyword generation triggered successfully');
      return true;
    } else {
      console.warn('⚠️ Keyword generation failed:', response.statusText);
      return false;
    }
  } catch (error) {
    console.warn('⚠️ Failed to trigger keyword generation:', error);
    // Non-blocking - don't throw error
    return false;
  }
};

/**
 * Determine business type from table name
 */
export const getBusinessTypeFromTable = (tableName: string): 'trader' | 'craftie' | 'hospitality' | 'professional' => {
  const normalized = tableName.toLowerCase();
  
  if (normalized.includes('craft')) {
    return 'craftie';
  } else if (normalized.includes('hospitality')) {
    return 'hospitality';
  } else if (normalized.includes('professional')) {
    return 'professional';
  }
  
  return 'trader';
};

export const keywordService = {
  triggerKeywordGeneration,
  getBusinessTypeFromTable
};
